// One content's details (a song, scene or story added with Add New Content), opened from Courses
// Management's content table and from Add New Content's saved tabs: where it is, its description,
// coins, status and items, each previewed in place. The course it is saved in isn't shown: Edit opens
// its page in edit mode (where content is edited), and a draft can be published from here.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Pencil, Send } from 'lucide-react';
import { api } from '../../api';
import { resultDialog } from '../../utils/swalDialogs';
import CourseItemPreview from './CourseItemPreview';
import { CvDialog } from './CourseStructureForms';
import { ITEM_TYPES, byOrder, canPreview, capitalise, coins, itemDetails } from './courseViewShared';
import { contentCoins } from './coursesShared';

const STATUS_TONE = { published: 'green', draft: 'amber', archived: 'gray' };

// `row` = { course, chapter } (contentRowsOf); `where` = "English › Action Songs › Vocal Songs".
// `onChanged()` runs after it is published.
export default function ContentDetailsDialog({ row, where, onClose, onChanged }) {
  const navigate = useNavigate();
  const { course } = row;
  const [chapter, setChapter] = useState(row.chapter);
  const [status, setStatus] = useState(course.status);
  const [preview, setPreview] = useState(null);
  const [publishing, setPublishing] = useState(false);

  // The list leaves quizzes as ids; the course itself comes with them (title, questions).
  useEffect(() => {
    let alive = true;
    api.get(`/api/v2/lms/admin/courses/${course._id}`)
      .then((res) => {
        const fresh = (res.data?.data?.modules || []).flatMap((module) => module.chapters || []).find((ch) => String(ch._id) === String(row.chapter._id));
        if (alive && fresh) setChapter(fresh);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [course._id, row.chapter._id]);

  const publish = async () => {
    setPublishing(true);
    try {
      await api.put(`/api/v2/lms/admin/courses/${course._id}/publish`);
      setStatus('published');
      resultDialog('success', 'Published', 'Students can see this content now.');
      onChanged?.();
    } catch (err) {
      const errors = err?.response?.data?.errors;
      resultDialog('error', 'Could not publish', errors?.length ? errors.join('\n') : err?.response?.data?.error || 'Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  // One dialog at a time: the item's preview takes its place, and closing it comes back here.
  if (preview) return <CourseItemPreview item={preview} onClose={() => setPreview(null)} />;

  const items = byOrder(chapter.contentItems);
  return (
    <CvDialog title={chapter.title || 'Untitled'} subtitle={where} label={`${chapter.title || 'Content'} details`} wide dismissible onClose={onClose}>
      <div className="cv-dialog__body">
        <div className="cd-meta">
          <span className={`cm-pill cm-pill--${STATUS_TONE[status] || 'gray'}`}>{capitalise(status)}</span>
          <span className="cv-coin-chip" title="Default coins + its items' coins + coin rules"><i>₹</i>{contentCoins(chapter)} ISF coins</span>
        </div>
        {chapter.description && <p className="cd-desc">{chapter.description}</p>}
        {(coins(chapter.defaultCoins) > 0 || chapter.coinRules?.length > 0) && (
          <div className="cv-rules">
            <span>Default coins: <b>{coins(chapter.defaultCoins)}</b></span>
            {(chapter.coinRules || []).map((rule, i) => <span key={`${rule.label}-${i}`}>{rule.label}: <b>{coins(rule.coins)}</b></span>)}
          </div>
        )}
        {items.length ? (
          <div className="cm-table-wrap">
            <table className="cm-table cv-items">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Content Type</th>
                  <th>Title / Details</th>
                  <th className="is-num">ISF Coins</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => {
                  const type = ITEM_TYPES[item.type] || ITEM_TYPES.file;
                  const details = itemDetails(item);
                  return (
                    <tr key={item._id || i}>
                      <td>{i + 1}</td>
                      <td><span className="ca-type"><i aria-hidden="true">{type.icon}</i>{type.label}</span></td>
                      <td>
                        <div className="cv-item">
                          <b>{item.title}</b>
                          {details && <small>{details}</small>}
                        </div>
                      </td>
                      <td className="is-num">{item.coins != null ? <span className="cv-coin-chip"><i>₹</i>{coins(item.coins)}</span> : '—'}</td>
                      <td>
                        <button type="button" className="ca-act ca-act--view" onClick={() => setPreview(item)} disabled={!canPreview(item)} aria-label={`View ${item.title}`}>
                          <Eye size={15} />View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="cv-note">Nothing has been added to this content yet.</p>
        )}
      </div>
      <footer className="cv-dialog__foot">
        {status === 'draft' && (
          <button type="button" className="cm-btn cm-btn--soft cm-btn--sm" onClick={publish} disabled={publishing}>
            <Send size={15} />{publishing ? 'Publishing…' : 'Publish'}
          </button>
        )}
        <button type="button" className="cm-btn cm-btn--soft cm-btn--sm" onClick={() => navigate(`/admin/courses/${course._id}/view?edit=1`)}>
          <Pencil size={15} />Edit
        </button>
        <button type="button" className="cm-btn cm-btn--purple cm-btn--sm" onClick={onClose}>Close</button>
      </footer>
    </CvDialog>
  );
}
